import { useTranslation } from 'react-i18next';
import { Badge, cn } from '@scio3/ui';
import type { BillingInterval } from '@scio3/shared-types';
import { formatCurrencyAmount } from '../utils/plan-helpers';

type BillingCycleToggleProps = {
  value: BillingInterval;
  onChange: (interval: BillingInterval) => void;
  monthlyPricePerScreen: number;
  yearlyPricePerScreen: number;
  screenCount: number;
  currency?: string;
};

/**
 * Payment term selection matching scio1's step 3 layout.
 * Two large side-by-side cards: Monthly and Yearly.
 * Selected card gets teal background.
 */
export function BillingCycleToggle({
  value,
  onChange,
  monthlyPricePerScreen,
  yearlyPricePerScreen,
  currency = 'usd',
}: BillingCycleToggleProps) {
  const { t } = useTranslation();

  const options = [
    {
      id: 'monthly' as const,
      label: t('MONTHLY'),
      price: formatCurrencyAmount(monthlyPricePerScreen, currency),
      subtitle: t('Per screen per month'),
      discountText: null as string | null,
      note: null as string | null,
    },
    {
      id: 'yearly' as const,
      label: t('YEARLY'),
      price: formatCurrencyAmount(yearlyPricePerScreen, currency),
      subtitle: t('Per screen per year'),
      discountText: t('10% off if paid yearly'),
      note: `*${t('No Free Devices')}`,
    },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold text-center">{t('Select Your Payment Term')}</h1>

      <div className="grid grid-cols-2 gap-4">
        {options.map((option) => {
          const isSelected = value === option.id;
          return (
            <div
              key={option.id}
              className={cn(
                'rounded-lg border-2 cursor-pointer transition-colors overflow-hidden',
                isSelected
                  ? 'border-primary bg-primary text-white'
                  : 'border-border bg-card hover:border-primary/50',
              )}
              onClick={() => onChange(option.id)}
            >
              <div className="p-6 text-center">
                {/* Title */}
                <h3
                  className={cn(
                    'text-sm font-bold tracking-wide mb-3',
                    isSelected ? 'text-white' : 'text-foreground',
                  )}
                >
                  {option.label}
                </h3>

                <hr className={cn('mb-4', isSelected ? 'border-white/30' : 'border-border')} />

                {/* Price */}
                <p
                  className={cn(
                    'text-3xl font-bold',
                    isSelected ? 'text-white' : 'text-foreground',
                  )}
                >
                  {option.price}
                </p>

                {/* Subtitle */}
                <p
                  className={cn(
                    'text-sm mt-3',
                    isSelected ? 'text-white/80' : 'text-muted-foreground',
                  )}
                >
                  {option.subtitle}
                </p>

                {/* Discount badge */}
                {option.discountText && (
                  <Badge className="mt-3 bg-amber-500 text-white hover:bg-amber-500">
                    {option.discountText}
                  </Badge>
                )}

                {/* Note */}
                {option.note && (
                  <p
                    className={cn(
                      'text-xs mt-3',
                      isSelected ? 'text-white/60' : 'text-muted-foreground',
                    )}
                  >
                    {option.note}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
